import { RegistrationForm } from '@components/form/templates/RegistrationForm';
import { useEmbed } from '@components/utils/contexts/EmbedContext';
import LinkButton from '@components/utils/cta/linkButton';
import Icon from '@components/utils/icons/icon';
import { COMMON } from '@constants';
import styled from '@emotion/styled';
import {
    OutboundLinkContext,
    OutboundLinkDestinationCategory,
    OutboundLinkShape,
} from '@enums';
import { RedirectionType } from '@graphql/generated/graphql';
import { useFormSubmit } from '@hooks';
import { useFlow } from '@lib/pimster-flow/contexts/FlowContext';
import { UIVariant, Button, VStack } from '@lib/uikit';
import { useTranslation } from 'next-i18next';
import { FC } from 'react';

import { useFlowPage } from '../contexts/FlowPageContext';
import {
    isRegistrationFlowPage,
    isSellFlowPage,
    isSuccessFlowPage,
} from '../utils/validators';

const CenteredIcon = styled.div`
    display: flex;
    justify-content: center;
`;

const StyledIcon = styled(Icon)`
    padding: ${({ theme }) => theme.spacing.xs};
    border-radius: ${({ theme }) => theme.shape.radius.round};
    background: rgba(255, 255, 255, 0.85);
    color: ${({ theme }) => theme.palette.success.main};
`;

export const FlowPageFooter: FC = () => {
    const { isRedirecting, isFlowCompleted, onNext } = useFlow();
    const { flowPage, ctaText, process } = useFlowPage();
    const { isFlowBundle } = useEmbed();

    const { t } = useTranslation(COMMON);

    const shouldExit = !(isFlowCompleted && isFlowBundle);

    const {
        error,
        isSubmitting,
        isSuccessful: isRegistered,
        onSubmit,
    } = useFormSubmit({
        formName: flowPage.Registration?.Form?.Name || '',
        formUID: flowPage.Registration?.Form?.UID || '',
        isProductRegistration:
            flowPage.Registration?.Form?.IsProductRegistration ?? false,
        onSuccess: shouldExit ? onNext : undefined,
    });

    return (
        <VStack spacing='sm'>
            {isSellFlowPage(flowPage) ? (
                <LinkButton
                    isFullWidth
                    type={RedirectionType.Outside}
                    url={flowPage.AdditionalProduct.Additional_product.Url!}
                    text={t('purchase')}
                    shape={OutboundLinkShape.Button}
                    context={OutboundLinkContext.Flow}
                    destinationName={
                        flowPage.AdditionalProduct.Additional_product.Name
                    }
                    destinationCategory={
                        OutboundLinkDestinationCategory.AdditionalProduct
                    }
                />
            ) : isRegistrationFlowPage(flowPage) &&
              (shouldExit || !isRegistered) ? (
                <RegistrationForm
                    formName={flowPage.Registration.Form.Name}
                    formUID={flowPage.Registration.Form.UID || ''}
                    customerInputType='ONBOARDING'
                    companyName={flowPage?.Product?.Company?.Name ?? ''}
                    ctaText={flowPage.Registration.Form.Elements[0].CtaText}
                    emailConsent={
                        flowPage.Registration.Form.Elements[0].EmailConsent
                    }
                    withCGU={flowPage.Registration.Form.Elements[0].WithCGU}
                    isDisabled={process?.isLoading}
                    isError={Boolean(error)}
                    isSubmitting={isSubmitting || isRedirecting}
                    onSubmit={onSubmit}
                    variant={UIVariant.DarkTranslucent}
                />
            ) : shouldExit ? (
                <Button
                    isFullWidth
                    disabled={process?.isLoading}
                    onClick={onNext}
                    colorScheme='white'
                >
                    {ctaText}
                </Button>
            ) : !isSuccessFlowPage(flowPage) ? (
                <CenteredIcon>
                    <StyledIcon name='check' size='xlarge' />
                </CenteredIcon>
            ) : undefined}
        </VStack>
    );
};
